<template>
  <div class="page home">
    <!-- Section Héroïque -->
    <section class="hero">
      <div class="hero-content">
        <p class="hero-kicker">{{ isEnglish ? 'Currency exchange and money transfer' : 'Change de devises et transfert d’argent' }}</p>
        <h1 class="hero-title">{{ isEnglish ? 'Fast, clear, and reliable currency service.' : 'Un service de change rapide, clair et fiable.' }}</h1>
        <p class="hero-description">
          {{ isEnglish ? 'Nyenyezia Forex helps customers exchange currencies, review current buy and sell rates, and get practical support for international money needs.' : "Nyenyezia Forex accompagne ses clients pour le change de devises, la consultation des taux achat/vente et les besoins de transfert d'argent." }}
        </p>
        <div class="hero-actions">
          <router-link :to="localizedPath('/taux-de-change')" class="cta-button">{{ isEnglish ? 'View Rates' : 'Voir les taux' }}</router-link>
          <router-link :to="localizedPath('/contact')" class="cta-button cta-secondary">{{ isEnglish ? 'Contact Us' : 'Nous contacter' }}</router-link>
        </div>
      </div>
    </section>

    <section class="trust-strip">
      <div class="trust-grid">
        <div class="trust-item" v-for="item in trustItems" :key="item.title">
          <span class="trust-icon">{{ item.icon }}</span>
          <div>
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Section sur le taux de change -->
    <section class="exchange-rate">
      <div class="currency-module">
        <div class="rates-card-header">
          <div>
            <p class="eyebrow">{{ isEnglish ? 'Current rates' : 'Taux actuels' }}</p>
            <h2 class="section-title">{{ isEnglish ? 'Exchange Rates' : 'Taux de change' }}</h2>
            <p class="section-lead">{{ isEnglish ? 'Simple buy and sell rates for the main currencies displayed by Nyenyezia Forex.' : 'Les taux achat et vente des principales devises affichées par Nyenyezia Forex.' }}</p>
          </div>
          <router-link :to="localizedPath('/taux-de-change')" class="cta-button">{{ isEnglish ? 'View All Rates' : 'Voir tous les taux' }}</router-link>
        </div>
        <table class="currency-table">
          <thead>
            <tr>
              <th style="text-align: left;">{{ isEnglish ? 'Currency' : 'Devise' }}</th>
              <th>{{ isEnglish ? 'Client Buy Rate' : 'Achat client' }}</th>
              <th>{{ isEnglish ? 'Client Sell Rate' : 'Vente client' }}</th>
              <!-- <th></th> -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="rate in homeDevises" :key="rate.currency_code">
              <td style="text-align: left;">
                <CountryFlag :iso="countryCodePerCurrency[rate.currency_code] || rate.currency_code.slice(0, 2)" mode="squared" />
                <p style="text-align: left;" class="currency-table-text">{{ rate.currency_code }} {{ displayCurrencyName(rate) }}</p></td>
                <td class="digital-text">
                {{ formatRate(rate.buy_rate) }}
              </td>
              <td class="digital-text">
                {{ formatRate(rate.sell_rate) }}
              </td>
              <!-- <td v-if="rateHistory[currency]">
                <canvas :id="`chart-${currency}`" width="100" height="50"></canvas>
              </td> -->
            </tr>
          </tbody>
        </table>
        <p class="rate-note">
          {{ isEnglish ? '*Rates are subject to change without notice' : '*Les taux sont susceptibles à des changements sans préavis' }}
        </p>
        <p class="rate-note">
          {{ isEnglish ? '*Specific cash denomination requests may be subject to additional fees' : "*Pour des demandes spécifiques de coupures certains frais peuvent s'appliquées" }}
        </p>
      </div>
    </section>

    <!-- Section des services -->
    <section class="services">
      <p class="eyebrow light">{{ isEnglish ? 'What we do' : 'Ce que nous faisons' }}</p>
      <h2 class="section-title section-title__services">{{ isEnglish ? 'Services built around your exchange needs' : 'Des services pensés pour vos besoins de change' }}</h2>
      <div class="services-list" data-aos="fade-top">
        <div  data-aos="zoom-in" class="service-card" v-for="(service, index) in displayedServices" :key="index">
          <img :src="service.icon" :alt="service.title" class="service-icon" />
          <h3 class="service-title">{{ service.title }}</h3>
          <p class="service-description">{{ service.description }}</p>
          <router-link :to="service.ctaTo" class="cta-button">{{ service.ctaText }}</router-link>
        </div>
      </div>
    </section>
    

    <!-- Section FAQ -->
    <section class="faq">
      <p class="eyebrow">{{ isEnglish ? 'Useful answers' : 'Réponses utiles' }}</p>
      <h2 class="section-title">{{ isEnglish ? 'Frequently Asked Questions' : 'Questions fréquentes' }}</h2>
      <div data-aos="fade-top"  class="faq-list">
        <div data-aos="zoom-in" class="faq-item" v-for="(faq, index) in displayedFaqs" :key="index">
          <h3 class="faq-question">{{ faq.question }}</h3>
          <p class="faq-answer">{{ faq.answer }}</p>
        </div>
      </div>
    </section>

    <!-- Section contact rapide -->
    <section class="contact-us">
      <h2 class="section-title">{{ isEnglish ? 'Need a rate or a transfer?' : 'Besoin d’un taux ou d’un transfert ?' }}</h2>
      <p class="contact-description">{{ isEnglish ? 'Contact Nyenyezia Forex and our team will guide you through the next step.' : "Contactez Nyenyezia Forex et notre équipe vous guidera pour la suite." }}</p>
      <router-link :to="localizedPath('/contact')" class="contact-button">{{ isEnglish ? 'Contact Us' : 'Contactez-Nous' }}</router-link>
    </section>
  </div>
</template>

<script>
import CountryFlag from '@dzangolab/vue-country-flag-icon'
  import '@dzangolab/vue-country-flag-icon/dist/CountryFlag.css' // import stylesheet
import { fetchPublicRates } from '@/services/ratesService';

export default {
  name: "HomePage",
  components: {CountryFlag},
  data() {
    return {
      homeDevises: [],
      devises_taux: [],
      countryCodePerCurrency: {
        "AED": "AE",
        "CHF": "CH",
        "GBP": "GB",
        "GMD": "GM",
        "CDF": "CD",
  "GNF": "GN",
  "KES": "KE",
  "LRD": "LR",
  "LYD": "LY",
  "MRU": "MR",
  "SDG": "SD",
  "SLE": "SL",
  "STN": "ST",
  "ZWL": "ZW",
  "AFN": "AF",
  "ALL": "AL",
  "DZD": "DZ",
  "AOA": "AO",
  "ARS": "AR",
  "AMD": "AM",
  "AWG": "AW",
  "AUD": "AU",
  "AZN": "AZ",
  "BDT": "BD",
  "BYN": "BY",
  "BZD": "BZ",
  "BMD": "BM",
  "BOB": "BO",
  "BAM": "BA",
  "BOV": "BO",
  "BRL": "BR",
  "BSD": "BS",
  "BTN": "BT",
  "BWP": "BW",
  "BND": "BN",
  "BGL": "BG",
  "BIF": "BI",
  "BBD": "BB",
  "CVE": "CV",
  "CLP": "CL",
  "CNY": "CN",
  "COP": "CO",
  "CRC": "CR",
  "HRK": "HR",
  "CYP": "CY",
  "CUP": "CU",
  "CZK": "CZ",
  "DKK": "DK",
  "DJF": "DJ",
  "DMA": "DM",
  "ECV": "EC",
  "EGP": "EG",
  "ERN": "ER",
  "ESP": "ES",
  "EST": "EE",
  "ETB": "ET",
  "EUR": "EU",
  "FJD": "FJ",
  "FOK": "FO",
  "GEL": "GE",
  "GGP": "GG",
  "GHS": "GH",
  "GTM": "GT",
  "GUY": "GY",
  "HKD": "HK",
  "HUF": "HU",
  "HNL": "HN",
  "HTG": "HT",
  "HKG": "HK",
  "IDR": "ID",
  "INR": "IN",
  "IQD": "IQ",
  "ISK": "IS",
  "ILS": "IL",
  "IRR": "IR",
  "ITL": "IT",
  "JMD": "JM",
  "JOD": "JO",
  "JPY": "JP",
  "KGS": "KG",
  "KHR": "KH",
  "KMF": "KM",
  "KPW": "KP",
  "KRW": "KR",
  "KWD": "KW",
  "KYD": "KY",
  "KZT": "KZ",
  "LAK": "LA",
  "LBP": "LB",
  "LKR": "LK",
  "LSL": "LS",
  "LTL": "LT",
  "LUX": "LU",
  "MAD": "MA",
  "MDL": "MD",
  "MGA": "MG",
  "MKD": "MK",
  "MMK": "MM",
  "MNT": "MN",
  "MOP": "MO",
  "MUR": "MU",
  "MVR": "MV",
  "MWK": "MW",
  "MXN": "MX",
  "MYR": "MY",
  "MZN": "MZ",
  "NAD": "NA",
  "NGN": "NG",
  "NIO": "NI",
  "NOK": "NO",
  "NPR": "NP",
  "NZD": "NZ",
  "OMR": "OM",
  "PAB": "PA",
  "PEN": "PE",
  "PGK": "PG",
  "PHP": "PH",
  "PKR": "PK",
  "PLN": "PL",
  "PRB": "PR",
  "PYG": "PY",
  "QAR": "QA",
  "RON": "RO",
  "RUB": "RU",
  "RWF": "RW",
  "SAR": "SA",
  "SBD": "SB",
  "SCR": "SC",
  "SEK": "SE",
  "SGD": "SG",
  "SHS": "SH",
  "SLL": "SL",
  "SOS": "SO",
  "SRD": "SR",
  "SSP": "SS",
  "STD": "ST",
  "SYP": "SY",
  "SZL": "SZ",
  "THB": "TH",
  "TJS": "TJ",
  "TMT": "TM",
  "TND": "TN",
  "TOP": "TO",
  "TRY": "TR",
  "TTD": "TT",
  "TVD": "TV",
  "TWD": "TW",
  "TZS": "TZ",
  "UAH": "UA",
  "UGX": "UG",
  "USD": "US",
  "UYU": "UY",
  "UZS": "UZ",
  "VEF": "VE",
  "VND": "VN",
  "VUV": "VU",
  "WST": "WS",
  "XAF": "CM",
  "XOF": "FR",
  "XPF": "PF",
  "YER": "YE",
  "ZAR": "ZA",
  "ZMW": "ZM",
  "ZWD": "ZW",
  "GYD": "AZ",
  "DOP": "DO",
  "ANG": "AO",
  "BGN": "BG",
  "BHD": "BH",
  "FKP": "FK",
  "GIP": "GI",
  "GTQ": "GT"

},
      fromCurrency: "CAD",
      rates: {},
      previousRates: {},
      rateHistory: {}, // Store historical rates for charting,
      services: [
        { 
          ctaText: "Demander ce service",
          ctaTo: "/contact?subject=exchange",
          icon: "images/trading.png",
          title: "Échange de devises",
          description: "Achat et vente de devises avec des taux affichés clairement et confirmés avant l’opération.",
        },
        {
          ctaText: "Nous-contacter",
          ctaTo: "/contact?subject=international-transfert",
          icon: "images/accounts.png",
          title: "Transfert d'Argent",
          description: "Accompagnement pour vos besoins de transfert avec un processus simple et un suivi humain.",
        },
        {
          ctaText: "Parler à l’équipe",
          ctaTo: "/contact?subject=consultation",
          icon: "images/analysis.png",
          title: "Consultation et accompagnement",
          description: "Aide pratique pour comprendre les taux, les options disponibles et les étapes à suivre.",
        },
      ],
      faqs: [
        {
          question: "Comment consulter les taux ?",
          answer: "Consultez la page Taux de change pour voir les taux achat et vente disponibles.",
        },
        {
          question: "Les taux peuvent-ils changer ?",
          answer: "Oui. Les taux peuvent évoluer selon le marché, la disponibilité des devises et les marges appliquées.",
        },
        {
          question: "Comment contacter Nyenyezia Forex ?",
          answer: "Utilisez la page Contact pour envoyer une demande avant votre opération de change ou de transfert.",
        },
      ],
      servicesEn: [
        {
          ctaText: "Contact Us",
          ctaTo: "/en/contact?subject=exchange",
          icon: "images/trading.png",
          title: "Currency Exchange",
          description: "Buy and sell currencies with rates displayed clearly and confirmed before the transaction.",
        },
        {
          ctaText: "Contact Us",
          ctaTo: "/en/contact?subject=international-transfert",
          icon: "images/accounts.png",
          title: "Money Transfer",
          description: "Support for money transfer needs with a simple process and human follow-up.",
        },
        {
          ctaText: "Talk to the Team",
          ctaTo: "/en/contact?subject=consultation",
          icon: "images/analysis.png",
          title: "Consultation and Guidance",
          description: "Practical help to understand rates, available options, and the next steps.",
        },
      ],
      faqsEn: [
        {
          question: "How can I check the rates?",
          answer: "Visit the Exchange Rates page to see available buy and sell rates.",
        },
        {
          question: "Can rates change?",
          answer: "Yes. Rates can change depending on the market, currency availability, and applied margins.",
        },
        {
          question: "How do I contact Nyenyezia Forex?",
          answer: "Use the Contact page to send a request before your exchange or transfer.",
        },
      ],
    };
  },
  computed: {
    isEnglish() {
      return this.$route.path.startsWith('/en');
    },
    displayedServices() {
      return this.isEnglish ? this.servicesEn : this.services;
    },
    displayedFaqs() {
      return this.isEnglish ? this.faqsEn : this.faqs;
    },
    trustItems() {
      return this.isEnglish ? [
        { icon: '01', title: 'Updated rates', description: 'Buy and sell rates are displayed clearly for customers.' },
        { icon: '02', title: 'Fast service', description: 'A practical process for currency exchange and transfer requests.' },
        { icon: '03', title: 'Customer support', description: 'Guidance before you confirm your exchange or transfer.' },
        { icon: '04', title: 'Clear display', description: 'Rates are organized for web, mobile, and public board viewing.' },
      ] : [
        { icon: '01', title: 'Taux mis à jour', description: 'Les taux achat et vente sont affichés clairement pour les clients.' },
        { icon: '02', title: 'Service rapide', description: 'Un processus simple pour les demandes de change et de transfert.' },
        { icon: '03', title: 'Accompagnement', description: 'Une équipe disponible avant la confirmation de votre opération.' },
        { icon: '04', title: 'Affichage clair', description: 'Des taux organisés pour le site, le mobile et le board public.' },
      ];
    }
  },
  props: {
    getPage: Function
  },
  methods: {
    async fetchRates() {
      try {
        this.homeDevises = (await fetchPublicRates()).slice(0, 5);
      } catch (error) {
        console.error("Erreur lors de la récupération des taux:", error);
      }
    },
    formatRate(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return '-';
      return number.toFixed(number >= 100 ? 2 : 6);
    },
    displayCurrencyName(rate) {
      if (!rate) return '';
      return this.isEnglish ? (rate.country_name_en || rate.currency_name) : (rate.country_name_fr || rate.currency_name);
    },
    localizedPath(path) {
      if (!this.isEnglish) return path;
      return path === '/' ? '/en' : '/en' + path;
    },
  },
  async mounted() {
    this.fetchRates();
  }
};
</script>

<style scoped>

@font-face {
  font-family: 'Digital-7';
  src: url('@/assets/fonts/digital-7.ttf') format('truetype');
  font-weight: normal;
  font-style: normal;
}

/* Optional: Global style for elements using the Digital-7 font */
.digital-text {
  font-family: 'Digital-7', sans-serif;
  color: #e53935;
  font-size: clamp(1.45rem, 2.4vw, 2rem);
  font-variant-numeric: tabular-nums;
}
.currency-table-text {
  color: #fff!important;
  display: inline-block;
  margin: 0 0 0 12px;
  vertical-align: middle;
}
.currency-table {
  margin: 24px auto 12px;
  border: none;
  border-spacing: 0;
  width: 100%;
  max-width: 900px;
  padding: 0;
  background: #10131a;
  border-radius: 8px;
  overflow: hidden;
}
.currency-table thead {
 background: #001041;
 color: #fff; 
 padding: 0;
}
.currency-table th {
  font-size: 0.85rem;
  letter-spacing: 0;
  padding: 14px 18px;
  text-transform: uppercase;
}
.flag-icon {
  width: 54px !important;
  height: 36px !important;
  float: none;
  vertical-align: middle;
}
.currency-table td {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 16px 18px;
  text-align: center;
}
.gold {
  color:#c39446;
}
/* Section Héroïque */
.hero {
  background:
    linear-gradient(90deg, rgba(255,255,255,0.94), rgba(255,255,255,0.78), rgba(255,255,255,0.2)),
    url("@/assets/hero-bg.jpg") no-repeat center center/cover;
  text-align: left;
  color: #001041;
  padding: 125px 24px 105px;
  animation: fadeIn 1.5s ease-in-out;
  overflow: hidden;
  border-bottom: 3px solid #001041;
}
.hero-content {
  max-width: 1180px;
  margin: 0 auto;
}
.hero-kicker {
  color: #c39446;
  font-size: 0.86rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  margin: 0 0 12px;
  text-transform: uppercase;
}
.hero-title {
  font-size: clamp(2.35rem, 5vw, 4.65rem);
  font-weight: bold;
  color: #001041;
  line-height: 1.04;
  margin-bottom: 18px;
  max-width: 760px;
}
.hero-description {
  font-size: 1.2rem;
  max-width: 560px;
  color: #001041;
  margin: 0 0 28px;
}
.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.cta-button {
  background-color: #c39446;
  color: white;
  display: inline-block;
  padding: 12px 20px;
  text-decoration: none;
  font-weight: bold;
  border-radius: 5px;
  transition: transform .2s ease, background .2s ease;
}
.cta-button:hover {
  background: #a97b2f;
  transform: translateY(-2px);
}
.cta-secondary {
  background: #001041;
}
.cta-secondary:hover {
  background: #061a58;
}

.trust-strip {
  background: #fff;
  border-bottom: 1px solid #dfe4ee;
  padding: 28px 20px;
}
.trust-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin: 0 auto;
  max-width: 1180px;
}
.trust-item {
  align-items: flex-start;
  background: #f7f9fc;
  border: 1px solid #e1e6ef;
  border-radius: 8px;
  display: flex;
  gap: 14px;
  padding: 18px;
}
.trust-icon {
  background: #001041;
  border-radius: 5px;
  color: #c39446;
  flex: 0 0 auto;
  font-weight: 800;
  padding: 6px 8px;
}
.trust-item h3 {
  color: #001041;
  font-size: 1rem;
  margin: 0 0 5px;
}
.trust-item p {
  color: #4d5a70;
  font-size: 0.92rem;
  line-height: 1.5;
  margin: 0;
}

/* Section Services */
.services {
  padding: 90px 20px 120px;
  text-align: center;
  background: #001041;
  color: #fff;
}
.services-list {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
}
.service-card {
  color: #001041;
  background: white;
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 8px;
  box-shadow: 0 18px 44px rgba(0,0,0,0.16);
  padding: 26px 22px;
  text-align: center;
  width: 300px;
  transition: transform 0.3s ease-in-out;
}
.service-card:hover {
  transform: translateY(-10px);
}
.service-card a {
  margin: 32px 0 10px !important;
  display: block;
}
.service-icon {
  width: auto;
  height: auto;
  margin: 20px 0;
}

/* Section Taux de change */
.exchange-rate {
  background-color: #f4f6f8;
  padding: 74px 20px;
}
.currency-module {
  background: #fff;
  border: 1px solid #d9dee7;
  border-radius: 8px;
  box-shadow: 0 12px 34px rgba(0, 16, 65, 0.08);
  margin: 0 auto;
  max-width: 980px;
  padding: 24px;
}
.rates-card-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.rate-table {
  margin-top: 20px;
}
.rate-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 20px;
  background: white;
  margin-bottom: 10px;
  border: 1px solid #ddd;
}
.section-title {
  color: #c39446;
  font-size: clamp(2rem, 4vw, 2.65rem);
  text-align: center;
  margin: 0;
}
.section-lead {
  color: #4b5870;
  margin: 8px 0 0;
  max-width: 560px;
}
.eyebrow {
  color: #001041;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 6px;
  text-transform: uppercase;
}
.eyebrow.light {
  color: #c39446;
  text-align: center;
}
.rate-note {
  color: #555;
  font-size: 0.9rem;
  margin: 6px 0 0;
}
.section-title__services {
  margin: 0 0 50px;
}
/* Section FAQ */
.faq {
  padding: 76px 20px;
  background-color: #f3f3f3;
  text-align: center;
}
.faq-list {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  max-width: 1080px;
  margin: 0 auto;
  text-align: left;
}
.faq-item {
  background: #fff;
  border: 1px solid #dce2ec;
  border-radius: 8px;
  box-shadow: 0 12px 30px rgba(0,16,65,0.08);
  margin: 0;
  padding: 22px;
}
.faq-question {
  font-weight: bold;
  color: #002147;
}
.faq-answer {
  margin-top: 5px;
}

/* Section Contact */
.contact-us {
  text-align: center;
  padding: 72px 20px;
  border-top: 3px solid #c39446;
}
.contact-button {
  background-color: #002147;
  color: white;
  padding: 10px 20px;
  text-decoration: none;
  font-weight: bold;
  border-radius: 5px;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes zoomIn {
  from {
    transform: scale(0.9);
  }
  to {
    transform: scale(1);
  }
}
/* Mobile Styles */
@media (max-width: 768px) {
  .hero {
    padding: 88px 20px;
    background:
      linear-gradient(90deg, rgba(255,255,255,0.96), rgba(255,255,255,0.82)),
      url("@/assets/hero-bg.jpg") no-repeat center center/cover;
  }
  .hero-actions,
  .rates-card-header {
    align-items: stretch;
    flex-direction: column;
  }
  .cta-button,
  .contact-button {
    text-align: center;
    width: 100%;
  }
  .trust-grid,
  .faq-list {
    grid-template-columns: 1fr;
  }
  .services {
    padding: 64px 16px 86px;
  }
  .currency-table {
    font-size: 13px;
  }
  .currency-table td {
    border: 1px solid #313131;

  }
  .flag-icon {
      width: 42px !important;
      height: 35px !important;
      margin-right: 10px;
  }
  .exchange-rate {
    padding: 32px 12px;
  }
  .currency-module {
    padding: 14px;
  }
  .currency-table th,
  .currency-table td {
    padding: 12px 8px;
  }
}
</style>
